import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '@/services/api';

export default {
    data() {
        return {
            apiLoading: {},
            apiErrors: {}
        };
    },

    methods: {
        // Generic API request handler
        async apiRequest(method, endpoint, data = null, options = {}) {
            const requestId = options.requestId || endpoint;
            const showToast = options.showToast !== false; // Default to true - always show error toasts unless explicitly disabled
            const showSuccessToast = options.showSuccessToast || false;
            const loadingKey = options.loadingKey || requestId;

            try {
                // Set loading state
                const apiLoading = { ...this.apiLoading };
                apiLoading[loadingKey] = true;
                this.$set(this, 'apiLoading', apiLoading);

                // Clear previous error for this request
                const apiErrors = { ...this.apiErrors };
                delete apiErrors[requestId];
                this.$set(this, 'apiErrors', apiErrors);

                // Build extra config from options
                const config = {
                    headers: options.headers || {},
                    ...options.axiosConfig
                };

                let response;
                const m = method.toLowerCase();
                if (m === 'get') {
                    response = await apiGet(endpoint, data, config);
                } else if (m === 'post') {
                    response = await apiPost(endpoint, data, config);
                } else if (m === 'put') {
                    response = await apiPut(endpoint, data, config);
                } else if (m === 'patch') {
                    response = await apiPatch(endpoint, data, config);
                } else if (m === 'delete') {
                    response = await apiDelete(endpoint, config);
                }

                // Show success toast if requested
                if (showSuccessToast) {
                    this._apiSuccessToast(options.successMessage || 'Operation completed successfully');
                }

                return response;

            } catch (error) {
                // Store error for component access
                const apiErrors = { ...this.apiErrors };
                apiErrors[requestId] = error;
                this.$set(this, 'apiErrors', apiErrors);

                // Show error toast
                if (showToast) {
                    this._apiErrorToast(error, options.errorTitle);
                }

                // Re-throw if specified
                if (options.rethrow) {
                    throw error;
                }

                return null;

            } finally {
                this._clearApiLoadingState(loadingKey);
            }
        },

        _clearApiLoadingState(loadingKey) {
            // Clear loading state
            const apiLoading = { ...this.apiLoading };
            apiLoading[loadingKey] = false;
            this.$set(this, 'apiLoading', apiLoading);
        },

        // GET request
        async apiGet(endpoint, params = null, options = {}) {
            return this.apiRequest('get', endpoint, params, options);
        },

        // POST request
        async apiPost(endpoint, data = null, options = {}) {
            return this.apiRequest('post', endpoint, data, options);
        },

        // PUT request
        async apiPut(endpoint, data = null, options = {}) {
            return this.apiRequest('put', endpoint, data, options);
        },

        // PATCH request
        async apiPatch(endpoint, data = null, options = {}) {
            return this.apiRequest('patch', endpoint, data, options);
        },

        // DELETE request
        async apiDelete(endpoint, options = {}) {
            return this.apiRequest('delete', endpoint, null, options);
        },

        // Error toast handler (private)
        _apiErrorToast(error, customTitle = null) {
            let message = 'An unexpected error occurred';
            let title = customTitle || 'API Error';

            // Handle different error scenarios
            if (error.response) {
                // Server responded with error status
                message = error.response.data?.message ||
                    error.response.data?.error ||
                    error.response.data ||
                    error.response.statusText;

                if (error.response.status && !customTitle) {
                    title += ` (${error.response.status})`;
                }

                // Handle specific status codes
                switch (error.response.status) {
                    case 401:
                        message = 'Authentication failed. Please log in again.';
                        break;
                    case 403:
                        message = 'You do not have permission to perform this action.';
                        break;
                    case 404:
                        message = 'The requested resource was not found.';
                        break;
                    case 422:
                        message = this.formatValidationErrors(error.response.data);
                        break;
                    case 500:
                        message = 'Server error. Please try again later.';
                        break;
                }
            } else if (error.request) {
                // Request made but no response received
                message = 'No response from server. Please check your connection.';
                title = 'Network Error';
            } else {
                // Error in request configuration
                message = error.message || message;
            }

            this.$bvToast.toast(message, {
                title,
                autoHideDelay: 5000,
                toaster: 'b-toaster-top-center',
                variant: 'danger',
                solid: true
            });
        },

        // Success toast handler (private)
        _apiSuccessToast(message, title = 'Success') {
            this.$bvToast.toast(message, {
                title,
                autoHideDelay: 3000,
                toaster: 'b-toaster-top-center',
                variant: 'success',
                solid: true
            });
        },

        // Format validation errors for 422 responses
        formatValidationErrors(data) {
            if (data.errors && typeof data.errors === 'object') {
                const errors = Object.values(data.errors).flat();
                return errors.join(', ');
            }
            return data.message || 'Validation failed';
        },

        // Get error for specific API call
        getApiError(key) {
            return this.apiErrors[key] || null;
        },

        // Clear all API errors
        clearApiErrors() {
            this.apiErrors = {};
        },

        // Retry failed request
        async apiRetry(method, endpoint, data = null, options = {}) {
            const maxRetries = options.maxRetries || 3;
            const retryDelay = options.retryDelay || 1000;
            let lastError;

            for (let i = 0; i < maxRetries; i++) {
                try {
                    return await this.apiRequest(method, endpoint, data, {
                        ...options,
                        showToast: i === maxRetries - 1 // Only show toast on last attempt
                    });
                } catch (error) {
                    lastError = error;
                    if (i < maxRetries - 1) {
                        await new Promise(resolve => setTimeout(resolve, retryDelay * (i + 1)));
                    }
                }
            }

            throw lastError;
        }
    }
};
